export type AvatarSize = 'x-small' | 'small' | 'medium' | 'large' | number;

const BASE_COLORS = {
  RED: '#D51C3F',
  ORANGE: '#D5751C',
  YELLOW: '#E2CD65',
  GREEN: '#659d68',
  BLUE: '#314D87',
  PURPLE: '#B166E3',
} as const;

const hexToHsl = (hex: string): [number, number, number] => {
  const r = Number.parseInt(hex.slice(1, 3), 16) / 255;
  const g = Number.parseInt(hex.slice(3, 5), 16) / 255;
  const b = Number.parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return [h * 360, s * 100, l * 100];
};

const hslToHex = (h: number, s: number, l: number): string => {
  h /= 360;
  s /= 100;
  l /= 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) {
      t += 1;
    }
    if (t > 1) {
      t -= 1;
    }
    if (t < 1 / 6) {
      return p + (q - p) * 6 * t;
    }
    if (t < 1 / 2) {
      return q;
    }
    if (t < 2 / 3) {
      return p + (q - p) * (2 / 3 - t) * 6;
    }
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;

  const r = Math.round(hue2rgb(p, q, h + 1 / 3) * 255);
  const g = Math.round(hue2rgb(p, q, h) * 255);
  const b = Math.round(hue2rgb(p, q, h - 1 / 3) * 255);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

const generateColorShades = (baseHex: string) => {
  const [h, s, l] = hexToHsl(baseHex);

  return [
    baseHex,
    hslToHex(h, s - 10, l - 10),
    hslToHex(h, s + 10, l + 10),
    hslToHex(h, s + 20, l + 20),
  ];
};

const AVATAR_COLORS = Object.entries(BASE_COLORS).reduce(
  (acc, [name, hex]) => {
    const [shade1, shade2, shade3, shade4] = generateColorShades(hex);

    acc[`${name}_1`] = shade1;
    acc[`${name}_2`] = shade2;
    acc[`${name}_3`] = shade3;
    acc[`${name}_4`] = shade4;
    return acc;
  },
  {} as Record<string, string>
);

const colors = Object.values(AVATAR_COLORS);

export const getAvatarColor = (identifier: string) => {
  const hash = identifier.split('').reduce((acc, char) => {
    return acc + (char.codePointAt(0) ?? 0);
  }, 0);
  return colors[hash % colors.length];
};
